import type { GooglePlace } from "@albertine/types";
import type { RecommendationItem } from "@albertine/types/openapi/lmt";
import { FitScoreAnalysis } from "@prototypes/ai-recommendations/FitScoreAnalysis";
import { ChatAlert } from "@prototypes/chat-alert/ChatAlert";
import { ImageStrip } from "@prototypes/image-strip/ImageStrip";
import classNames from "classnames";
import { Spinner } from "@/components/ui/spinner";
import { categoryDisplayName } from "../../common/types/OfferingCategory";
import {
    googleAddressSearchUrl,
    googlePlaceIdUrl,
} from "../../common/utils/google/GooglePlaceService";
import { CategoryIconMap } from "../Common/Icon";
import {
    PlaceDetailsPrimary,
    type PlaceDetailsPrimaryData,
} from "./CatalogItemDetails/CatalogItemDetailsPrimary";
import "./CatalogItemView.css";
import GoogleMap from "./GooglePlace/GoogleMap";

type WebRecommendationItem = Extract<RecommendationItem, { source: "web" }>;

type WebItemViewProps = {
    className?: string;
    item: WebRecommendationItem;
    place: GooglePlace | undefined;
    isLoadingPlace: boolean;
};

export function WebItemView(props: WebItemViewProps) {
    const { className, item, place, isLoadingPlace } = props;
    const description = resolveDescription(item);
    const photos = place?.photos ?? [];
    const markerPosition = place?.location
        ? { lat: place.location.latitude, lng: place.location.longitude }
        : undefined;
    const viewport = place?.viewport ? toLatLngBounds(place.viewport) : undefined;

    if (isLoadingPlace) {
        return (
            <div
                className={classNames(
                    "catalog-view catalog--loading flex w-full flex-col items-center justify-center",
                    className,
                )}
            >
                <Spinner className="size-6" />
            </div>
        );
    }

    return (
        <div
            className={classNames("catalog-view flex w-full flex-col gap-4", className)}
        >
            <div className="catalog-item__main-content flex w-full flex-col gap-2">
                {place && photos.length > 0 && (
                    <ImageStrip
                        images={photos.flatMap((photo, index) =>
                            photo.photoUri
                                ? [
                                      {
                                          id: photo.name ?? `photo-${index}`,
                                          src: photo.photoUri,
                                          href: photo.photoUri,
                                          alt: "",
                                          width: photo.widthPx,
                                          height: photo.heightPx,
                                      },
                                  ]
                                : [],
                        )}
                    />
                )}

                <div
                    key={item.id}
                    className="w-full animate-web-result-alert motion-reduce:animate-none"
                >
                    <ChatAlert
                        variant="information"
                        message="This result is not in the catalog yet; it's AI-generated from the web. Verify content before sending."
                        className="h-auto min-h-10 w-full py-2.5 [&>div>span]:whitespace-normal [&>div>span]:leading-body-sm"
                    />
                </div>

                <div className="catalog-item__details flex w-full flex-col gap-4">
                    {place && (
                        <PlaceDetailsPrimary
                            {...googlePlaceToDetailsData(place, item.category)}
                        />
                    )}

                    <FitScoreAnalysis
                        className="pt-4"
                        fitScore={item.score}
                        whyRelevant={item.rationale?.whyRelevant ?? undefined}
                        bestUseCase={item.rationale?.bestUseCase ?? undefined}
                        potentialRisks={item.rationale?.potentialRisks ?? undefined}
                    />

                    <div className="flex flex-col gap-6">
                        <h3>Description</h3>
                        {description ? (
                            <p className="whitespace-pre-wrap text-sm text-zinc-900 leading-5">
                                {description}
                            </p>
                        ) : (
                            <p className="text-sm text-zinc-400 leading-5">
                                What is this place like? Who is it for?
                            </p>
                        )}
                    </div>

                    {markerPosition && viewport ? (
                        <GoogleMap
                            mapId="catalog-beta_view__web-item"
                            markerTitle={item.name}
                            markerPosition={markerPosition}
                            viewport={viewport}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function resolveDescription(item: WebRecommendationItem): string | undefined {
    if (item.description?.trim()) {
        return item.description.trim();
    }
    return item.rationale?.whyRelevant?.trim() || undefined;
}

function toLatLngBounds(
    viewport: NonNullable<GooglePlace["viewport"]>,
): google.maps.LatLngBounds | undefined {
    if (!viewport.low || !viewport.high || !window.google) {
        return undefined;
    }

    return new google.maps.LatLngBounds(
        { lat: viewport.low.latitude, lng: viewport.low.longitude },
        { lat: viewport.high.latitude, lng: viewport.high.longitude },
    );
}

function googlePlaceToDetailsData(
    place: GooglePlace,
    category: WebRecommendationItem["category"],
): PlaceDetailsPrimaryData {
    const offeringCategories = {
        primaryIcon: CategoryIconMap[category],
        text: categoryDisplayName(category, "en"),
    };

    const address = place.formattedAddress ?? undefined;

    return {
        offeringCategories,
        website: place.websiteUri ? place.websiteUri.split("?")[0] : undefined,
        phoneNumber: place.internationalPhoneNumber ?? undefined,
        address,
        addressUrl: address ? googleAddressSearchUrl(address) : undefined,
        googlePlaceUrl:
            place.googleMapsUri ?? (place.id ? googlePlaceIdUrl(place.id) : undefined),
        showAddress: true,
        googleReview: {
            rating: place.rating,
            reviewsTotal: place.userRatingCount,
            googlePlaceId: place.id,
        },
        openingHours: undefined,
        businessStatus: place.businessStatus,
    };
}
